<template>
    <div>
        <v-container grid-list-xs>
            <v-row>
                <v-col>
                    <v-sheet class="pa-5" elevation="3">
                        <h4 class="mb-2 primary--text">{{ $t("results.congratulations") }}</h4>
                        <p class="mb-5">
                            {{ $t("approved.message", { approvalAmount: formattedApprovalAmount }) }}
                        </p>

                        <v-row class="mt-3" dense>
                            <v-col cols="12" sm="12" class="text-left">
                                <div class="ppv-sub-header body-large-1 mb-2">
                                    <span class="nowrap">
                                        {{ $t("plan.sub-header") }}
                                    </span>
                                </div>
                            </v-col>

                            <v-col sm="4">
                                <Form class="ppv-calculator mt-4 mt-sm-0" @submit.prevent="handleCalculatorSubmitted">
                                    <PaymentPlanEstimator
                                        ref="estimator"
                                        :value="calculatorAmountDollars"
                                        :max="maxCalculatorValue"
                                        :min="minCalculatorValue"
                                        :step="calculatorStep"
                                        @step="handleCalculatorStepped"
                                        @type="handleCalculatorTyped"
                                    />
                                </Form>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col>
                                <v-radio-group hide-details :value="paymentPlan">
                                    <PaymentPlanOption
                                        v-if="paymentPlan"
                                        :key="paymentPlan.providerPlanCode"
                                        :payment-plan="paymentPlan"
                                        :financed-amount-cents="paymentPlan.financedAmountCents"
                                        :provider-key="paymentPlan.providerKey"
                                    />
                                </v-radio-group>
                            </v-col>
                        </v-row>

                        <div v-if="paymentPlan && !atHome" class="text-left mt-4">
                            <div class="header">
                                <h4>{{ paymentPlan.durationMonths }}-Month Agreement</h4>
                            </div>
                            <div class="body-large-1">Full-Term <i>(Standard)</i></div>
                        </div>

                        <div v-if="paymentPlan" class="payment-container text-left">
                            <v-row>
                                <v-col cols="8" sm="4">
                                    <strong>Down Payment</strong>
                                </v-col>
                                <v-col cols="4" sm="3">
                                    {{ paymentPlan.downPaymentCents | currency }}
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="8" sm="4">
                                    <strong
                                        >{{ paymentPlan.numberOfPaymentsWithSameAmount }}
                                        {{ frequencyLabel }} Payments</strong
                                    >
                                </v-col>
                                <v-col cols="4" sm="3">
                                    {{ paymentPlan.monthlyAmountCents | currency }}
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="8" sm="4">
                                    <strong>1 Final Payment</strong>
                                </v-col>
                                <v-col cols="4" sm="3">
                                    {{ paymentPlan.lastPaymentAmountCents | currency }}
                                </v-col>
                            </v-row>
                            <v-row v-if="paymentPlan.financeChargeCents">
                                <v-col cols="8" sm="4">
                                    <strong>Origination Fee</strong><br />
                                    <i>Included in payments</i>
                                </v-col>
                                <v-col cols="4" sm="3">
                                    {{ paymentPlan.financeChargeCents | currency }}
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="8" sm="4">
                                    <strong>Amount Financed</strong>
                                </v-col>
                                <v-col cols="4" sm="3">
                                    {{ paymentPlan.financedAmountCents | currency }}
                                </v-col>
                            </v-row>
                        </div>

                        <div v-if="paymentPlan" class="payment-container text-left">
                            <v-divider class="my-5"></v-divider>
                            <v-row>
                                <v-col cols="8" sm="4">
                                    <strong>APR</strong>
                                </v-col>
                                <v-col cols="4" sm="3"> {{ paymentPlan.apr.toFixed(2) }}% </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="8" sm="4">
                                    <strong>Term</strong>
                                </v-col>
                                <v-col cols="4" sm="3"> {{ paymentPlan.durationMonths }} Months </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="8" sm="4">
                                    <strong>1st Payment Due</strong>
                                </v-col>
                                <v-col cols="4" sm="3">
                                    {{ formattedFirstPaymentDueDate }}
                                </v-col>
                            </v-row>
                        </div>
                        <v-row>
                            <v-col>
                                <div id="payment-plan-review-footer">
                                    <span v-if="!isRISA">{{ $t(`paymentPlanReview.footer.finwise`) }}</span>
                                    <span v-if="isRISA">{{ $t(`paymentPlanReview.footer.risa`) }}</span>
                                </div>
                            </v-col>
                        </v-row>
                        <v-card-actions v-if="!atHome || $vuetify.breakpoint.xsOnly" class="py-6 px-0">
                            <v-row>
                                <v-col :class="{ 'text-right': !atHome, 'text-left': atHome }">
                                    <SubmitButton class="secondary" rounded @click="next">
                                        {{ $t("action.continue") }}
                                    </SubmitButton>
                                </v-col>
                            </v-row>
                        </v-card-actions>
                    </v-sheet>
                </v-col>
            </v-row>
        </v-container>
        <ConfirmSelectionModal
            :show-modal="showConfirmModal"
            :amount="calculatorAmountDollars"
            @confirm-less-amount="lessAmountConfirmation"
            @close-confirm-modal="closeConfirmModal"
        />
        <SameAsCashModal
            :show-modal="showSameAsCashModal"
            @same-as-cash-confirm="sameAsCashConfirmation"
            @close-confirm-modal="closeSameAsCashModal"
        />
        <PaymentRecalculatingModal :show="showRecalculatingModal" :frequency="blankFrequency" />
        <AtHomeFinishModal :show="showAtHomeFinishModal" />
    </div>
</template>

<script>
    import { centsToDollars, dollarsToCents, formatCurrency } from "@/util/currency"
    import { applicationState, providerState, sessionState } from "@/state"
    import { getMessagesApplyMode } from "@/model/apply/ApplyMode"
    import { getCalculatorCents } from "@/util/calculator"
    import { applyService, workflowService } from "@/service"
    import debounce from "lodash/debounce"
    import Providers from "@/model/provider/Providers"
    import AtHomeFinishModal from "@/components/modal/AtHomeFinishModal.vue"
    import ConfirmSelectionModal from "@/components/modal/ConfirmSelectionModal.vue"
    import PaymentRecalculatingModal from "@/components/modal/PaymentRecalculatingModal.vue"
    import SameAsCashModal from "@/components/modal/SameAsCashModal.vue"
    import { formatDate } from "@/util/date"
    import SelectPaymentPlanAction from "@/action/actions/SelectPaymentPlanAction"
    import { mapState } from "vuex"
    import DynamicProviderKey from "@/model/provider/DynamicProviderKey"

    export default {
        components: {
            AtHomeFinishModal,
            ConfirmSelectionModal,
            PaymentRecalculatingModal,
            SameAsCashModal,
        },

        data() {
            const application = applicationState.application

            let response = providerState.getApplyResponse()

            let approvedAmountCents = response.approvedAmountCents

            let calculatorAmount = centsToDollars(
                getCalculatorCents(approvedAmountCents, applicationState.application.purchaseAmountCents)
            )

            return {
                applyMode: applicationState.applyMode,
                approvedAmountCents,
                calculatorAmount,
                calculatorStep: 200,
                formattedPurchaseAmount: formatCurrency(application.purchaseAmountCents),
                formattedApprovalAmount: formatCurrency(approvedAmountCents),
                paymentPlan: null,
                response,
                selectedProviderKey: response.providerKey,
                selectedProviderInfo: Providers[DynamicProviderKey.extractProviderKey(response.providerKey)],
                showAtHomeFinishModal: false,
                showConfirmModal: false,
                showRecalculatingModal: false,
                showSameAsCashModal: false,
                sameAsCashConfirmed: false,
                lessAmountConfirmed: false,
                atHome: sessionState.atHome,
            }
        },
        computed: {
            ...mapState("provider", {
                minPurchaseAmount: state => {
                    return state.providers.find(provider => provider.providerKey === sessionState.providerKey)
                        ?.minPurchaseAmountCents
                },
            }),

            calculatorAmountDollars: {
                get: function () {
                    return this.calculatorAmount
                },
                set: function (val) {
                    this.calculatorAmount = val
                },
            },

            calculatorAmountCents() {
                return (this.calculatorAmountDollars || 0) * 100
            },

            confirmLess() {
                return (
                    this.calculatorAmountDollars < centsToDollars(this.approvedAmountCents) &&
                    this.calculatorAmountDollars < centsToDollars(this.purchaseAmountCents)
                )
            },

            formattedFirstPaymentDueDate() {
                return formatDate(this.paymentPlan.firstPaymentDate, "semiVerboseDate")
            },

            blankFrequency() {
                return ""
            },

            downPaymentAmountCents() {
                return this.paymentPlan?.downPaymentCents ?? this.selectedProviderInfo.maxDownPaymentCents
            },

            frequencyLabel() {
                switch (this.paymentPlan?.payFrequency) {
                    case "weekly":
                        return "Weekly"
                    case "bi_weekly":
                        return "Bi-Weekly"
                    case "monthly":
                        return "Monthly"
                    case "semi_monthly":
                        return "Semi-Monthly"
                    default:
                        return null
                }
            },

            isRISA() {
                return this.paymentPlan?.typeId === "RISA"
            },

            finType() {
                return this.paymentPlan?.typeId.lowerCase
            },

            maxCalculatorValue() {
                return centsToDollars(getCalculatorCents(this.approvedAmountCents, this.purchaseAmountCents))
            },

            minCalculatorValue() {
                return centsToDollars(
                    this.minPurchaseAmount - this.selectedProviderInfo.maxDownPaymentCents + this.downPaymentAmountCents
                )
            },

            messagesApplyMode() {
                return getMessagesApplyMode(this.applyMode)
            },

            purchaseAmountCents() {
                return applicationState.application.purchaseAmountCents || this.approvedAmountCents
            },
        },

        async created() {
            await this.getPaymentPlan()
        },

        methods: {
            async getPaymentPlan() {
                if (!this.selectedProviderKey) {
                    return
                }

                this.showRecalculatingModal = !!this.selectedProviderInfo.paymentCalculatorRecalculatingModal

                try {
                    let amountCents = dollarsToCents(this.calculatorAmount)

                    const { paymentPlans } = await applyService.getPaymentPlans(this.selectedProviderKey, {
                        amountCents: amountCents,
                    })

                    providerState.setPaymentPlans(this.selectedProviderKey, paymentPlans)

                    this.paymentPlan = paymentPlans[0]
                } finally {
                    this.showRecalculatingModal = false
                }
            },

            getPaymentPlanLater: debounce(function () {
                this.publishPaymentPlansPresented(this.selectedProviderKey)
                this.getPaymentPlan()
            }, 1000),

            async submit() {
                const financedAmount = dollarsToCents(this.calculatorAmountDollars)
                applicationState.application.tentativePurchaseAmountCents = financedAmount
                applicationState.application.purchaseAmountCents = financedAmount
                const atHome = sessionState.atHome
                await workflowService.invoke(
                    new SelectPaymentPlanAction(this.paymentPlan, this.estimatedFinancedAmountCents, !atHome)
                )
                if (atHome) {
                    this.showAtHomeFinishModal = true
                } else {
                    await this.$router.push(`/providers/${sessionState.providerKey}/agreement`)
                }
            },

            next() {
                if (this.confirmLess) {
                    this.showConfirmModal = true
                } else if (!this.sameAsCashConfirmed) {
                    this.showSameAsCashModal = true
                } else {
                    this.submit()
                }
            },

            confirmedSelection() {
                if (!this.sameAsCashConfirmed) {
                    this.showSameAsCashModal = true
                } else {
                    this.submit()
                }
            },

            lessAmountConfirmation() {
                this.lessAmountConfirmed = true
                this.confirmedSelection()
            },

            sameAsCashConfirmation() {
                this.sameAsCashConfirmed = true
                this.confirmedSelection()
            },

            closeConfirmModal() {
                this.showConfirmModal = false
            },

            closeSameAsCashModal() {
                this.showSameAsCashModal = false
            },

            handleCalculatorTyped(value) {
                this.calculatorAmountDollars = value
            },

            handleCalculatorStepped(value) {
                this.$refs.estimator.blur()
                this.calculatorAmountDollars = value
                this.getPaymentPlanLater()
            },

            handleCalculatorSubmitted() {
                this.$refs.estimator.blur()
                this.publishPaymentPlansPresented(this.selectedProviderKey)
                this.getPaymentPlan()
            },

            async publishPaymentPlansPresented(providerKey) {
                await applyService.publishPaymentPlansPresented(providerKey)
            },
        },
    }
</script>

<style scoped lang="scss">
    #payment-options-message {
        display: flex;
        position: relative;
        justify-content: space-between;
        align-items: flex-start;
        color: $grey-darken-2;
    }

    #approval-container {
        display: flex;
        flex-direction: column;
        background-color: #f2f3f4;
        justify-content: center;
        align-content: center;
        height: to_rem(104);
        width: 238px;
        border-radius: 8px;
    }

    #payment-plan-review-footer {
        margin-top: 20px;
    }

    .header-text-container {
        text-align: left;
    }

    #approval-container {
        position: absolute;
        right: 4px;
        top: 0;
    }

    h4 {
        font-weight: 600 !important;
        font-size: to_rem(22px) !important;
        line-height: to_rem(28px) !important;

        color: #002855;
    }

    .body-large-1 {
        font-size: 18px;
        line-height: 28px;
        font-weight: bold;
        color: $primary-color;
    }

    .treatment-total {
        font-size: 16px;
        line-height: 24px;
        font-weight: bold;
        margin: 0;
    }

    .payment-container {
        border-radius: 8px;
        margin-top: 20px;
        padding-left: 20px;
    }

    .highlighted {
        font-size: to_rem(16px);
        color: $secondary-color;
        font-weight: 700;
    }

    .not-covered {
        color: #cf2a2a;
        font-size: 14px;
        line-height: 20px;
    }
</style>
